import React from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons/faCoins';
import { inflect } from 'inflection';

import SocialShare from 'buyside/components/presentation/SocialShare/socialShare';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import LoadingSpinner, {
  TYPES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import Tabs, { TabContent, TabNav } from 'buyside/components/presentation/Tabs/tabsComponents';

import { Maybe, useCurrentUserIdeaTokensQuery } from 'interfaces/graphql-typings';

import {
  accessIdeasUrl,
  ideasUrl,
  userReadingListUrl,
  inviteMembersUrl,
  userReferralsUrl
} from 'buyside/util/urlHelper';

const { maxRewardTokenCount } = require('buyside/util/constantsFromRails.json');

import styles from 'buyside/components/presentation/LeftSidebar/leftSidebarStyles.scss';

const IdeaTokenWidget: React.FC<{ readingListItemsCount: number }> = ({
  readingListItemsCount
}): Maybe<React.ReactElement> => {
  const { error, data, loading } = useCurrentUserIdeaTokensQuery();

  if (error) throw error;
  if (loading) return <LoadingSpinner type={TYPES.SECONDARY} />;
  if (!data || !data.currentUser) return null;

  const {
    id,
    availableTokenCount,
    trialTokenCount,
    trialTokenExpiresIn,
    usedTokenCount
  } = data.currentUser;

  return (
    <div>
      <div className={styles.creditHeading}>
        <FontAwesomeIcon icon={faCoins} className={styles.icon} />
        Idea Credits
      </div>
      <Tabs>
        <TabNav>
          <div>Balance</div>
          <div>Earned</div>
        </TabNav>
        <TabContent>
          <section className={styles.creditContainer}>
            <div className={styles.creditList}>
              {Boolean(trialTokenCount) && (
                <div className={styles.creditItem}>
                  <div>
                    Trial <span className={styles.red}>(Expires in {trialTokenExpiresIn})</span>
                  </div>
                  <div>
                    <strong>{trialTokenCount}</strong> {inflect('credit', trialTokenCount || 0)}
                  </div>
                </div>
              )}
              <div className={styles.creditItem}>
                <div>Used</div>
                <div>
                  <strong>{usedTokenCount}</strong> {inflect('credit', usedTokenCount || 0)}
                </div>
              </div>
            </div>
            <div className={classnames(styles.creditList, styles.noBorder)}>
              <div className={styles.creditItem}>
                <div>Total Available</div>
                <div>
                  <strong>{availableTokenCount}</strong>{' '}
                  {inflect('credit', availableTokenCount || 0)}
                </div>
              </div>
            </div>
            <div className={styles.ctaButtons}>
              <Button
                className={styles.btnOverride}
                size={BUTTON_SIZES.SMALL}
                text="Explore research database"
                type={BUTTON_TYPES.PRIMARY}
                url={ideasUrl()}
              />
              <Button
                className={styles.btnOverride}
                size={BUTTON_SIZES.SMALL}
                text="Purchase more credits"
                type={BUTTON_TYPES.MAJOR}
                url={accessIdeasUrl()}
              />
              <Button
                size={BUTTON_SIZES.SMALL}
                text={`View your unlocked reports (${readingListItemsCount})`}
                url={userReadingListUrl()}
              />
            </div>
          </section>
          <section className={styles.creditContainer}>
            <p className={styles.earnedMessage}>
              Refer your friends and earn up to {maxRewardTokenCount} credits.
            </p>
            <div className={styles.ctaButtons}>
              <Button
                className={styles.btnOverride}
                size={BUTTON_SIZES.SMALL}
                text="Send Invites"
                type={BUTTON_TYPES.PRIMARY}
                url={inviteMembersUrl()}
              />
              <SocialShare id={id} />
              <Button
                size={BUTTON_SIZES.SMALL}
                text="Track Your Progress"
                url={userReferralsUrl()}
              />
            </div>
          </section>
        </TabContent>
      </Tabs>
    </div>
  );
};

export default IdeaTokenWidget;
